/**
 * Phones, as this app calls the API: the Google account behind them, each customer's binding to it,
 * policy templates, enrollment codes, and what can be done to one phone.
 *
 * Kept apart from `crm.ts` because it is one self-contained surface; the conventions are the same - a
 * refusal throws an `ApiError` carrying the server's sentence, except the probes and commands, which
 * answer with a result whatever happens so a button never leaves its status line on "Working…".
 */
import type {
  AndroidAccountDto,
  AndroidEnterpriseDto,
  AndroidSetupResult,
  AndroidSignupDto,
  AndroidTestRequest,
  AndroidTestResult,
  DeviceDto,
  MobileCommandRequest,
  MobileCommandResult,
  MobileEnrollmentDto,
  MobileEnrollmentRequest,
  MobileLocationDto,
  MobilePolicyDto,
  MobilePolicySaveResult,
  RemoveMobileManagementRequest,
  SaveMobilePolicyRequest,
  UpdateAndroidAccountRequest
} from '../contracts.gen.js';
import { ApiError } from './auth.js';
import { request } from './http.js';

function reason(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message || fallback;
  return `The CRM could not be reached. (${error instanceof Error ? error.message : String(error)})`;
}

// ---- The Google account ----

export function getAndroidAccount(signal?: AbortSignal): Promise<AndroidAccountDto> {
  return request<AndroidAccountDto>('/api/android/account', { signal });
}

export function updateAndroidAccount(req: UpdateAndroidAccountRequest): Promise<AndroidAccountDto> {
  return request<AndroidAccountDto>('/api/android/account', { method: 'PUT', body: req });
}

export async function testAndroidAccount(req: AndroidTestRequest): Promise<AndroidTestResult> {
  try {
    return await request<AndroidTestResult>('/api/android/account/test', { method: 'POST', body: req });
  } catch (error) {
    return { success: false, message: reason(error, 'The test could not be run.'), managementReachable: false, subscriptionReachable: false };
  }
}

export async function setupAndroidPubSub(): Promise<AndroidSetupResult> {
  try {
    return await request<AndroidSetupResult>('/api/android/account/setup', { method: 'POST' });
  } catch (error) {
    return { success: false, message: reason(error, 'The set-up could not be run.'), topicName: '', subscriptionName: '' };
  }
}

export async function syncAndroid(): Promise<MobileCommandResult> {
  try {
    return await request<MobileCommandResult>('/api/android/sync', { method: 'POST' });
  } catch (error) {
    return { success: false, message: reason(error, 'Google could not be read.') };
  }
}

// ---- A customer's enterprise ----

export function getAndroidEnterprise(customerId: number, signal?: AbortSignal): Promise<AndroidEnterpriseDto> {
  return request<AndroidEnterpriseDto>(`/api/customers/${customerId}/android`, { signal });
}

export function connectAndroid(customerId: number): Promise<AndroidSignupDto> {
  return request<AndroidSignupDto>(`/api/customers/${customerId}/android/connect`, { method: 'POST' });
}

export function disconnectAndroid(customerId: number): Promise<void> {
  return request<void>(`/api/customers/${customerId}/android`, { method: 'DELETE' });
}

// ---- Policy templates ----

export async function getMobilePolicies(customerId: number, signal?: AbortSignal): Promise<MobilePolicyDto[]> {
  return (await request<MobilePolicyDto[] | null>(`/api/customers/${customerId}/android/policies`, { signal })) ?? [];
}

export function createMobilePolicy(customerId: number, req: SaveMobilePolicyRequest): Promise<MobilePolicySaveResult> {
  return request<MobilePolicySaveResult>(`/api/customers/${customerId}/android/policies`, { method: 'POST', body: req });
}

export function updateMobilePolicy(policyId: number, req: SaveMobilePolicyRequest): Promise<MobilePolicySaveResult> {
  return request<MobilePolicySaveResult>(`/api/android/policies/${policyId}`, { method: 'PUT', body: req });
}

export function deleteMobilePolicy(policyId: number): Promise<void> {
  return request<void>(`/api/android/policies/${policyId}`, { method: 'DELETE' });
}

// ---- Enrollment ----

export function createMobileEnrollment(customerId: number, req: MobileEnrollmentRequest): Promise<MobileEnrollmentDto> {
  return request<MobileEnrollmentDto>(`/api/customers/${customerId}/android/enrollment`, { method: 'POST', body: req });
}

// ---- One phone ----

export async function sendMobileCommand(deviceId: number, req: MobileCommandRequest): Promise<MobileCommandResult> {
  try {
    return await request<MobileCommandResult>(`/api/devices/${deviceId}/mobile/command`, { method: 'POST', body: req });
  } catch (error) {
    return { success: false, message: reason(error, 'The command could not be sent.') };
  }
}

export function assignMobilePolicy(deviceId: number, policyId: number): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${deviceId}/mobile/policy`, { method: 'POST', body: { policyId } });
}

export function refreshMobileDevice(deviceId: number): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${deviceId}/mobile/refresh`, { method: 'POST' });
}

export function republishMobilePolicy(deviceId: number): Promise<DeviceDto> {
  return request<DeviceDto>(`/api/devices/${deviceId}/mobile/publish`, { method: 'POST' });
}

export async function getMobileLocations(deviceId: number, signal?: AbortSignal): Promise<MobileLocationDto[]> {
  return (await request<MobileLocationDto[] | null>(`/api/devices/${deviceId}/mobile/locations`, { signal })) ?? [];
}

export async function removeMobileManagement(deviceId: number, req: RemoveMobileManagementRequest): Promise<MobileCommandResult> {
  try {
    return await request<MobileCommandResult>(`/api/devices/${deviceId}/mobile/remove`, { method: 'POST', body: req });
  } catch (error) {
    return { success: false, message: reason(error, 'Management could not be removed.') };
  }
}
